import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, EnvelopeFill, GeoAltFill, TelephoneFill, Whatsapp, SendFill } from "react-bootstrap-icons";
import language from "../../data/language";
import notify, { apiFieldErrors, currentLanguage } from "../../common/ui/notify";
import PageHero from "../../common/ui/PageHero";
import { EMAIL, MAP_URL, PHONE, PHONE_HREF, WHATSAPP_URL } from "../../common/ui/ContactStrip";
import "../../common/ui/ui.css";
import "./Contact.css";

const EMPTY_FORM = {name: '', email: '', phone: '', subject: '', message: ''};

// Same place as before, but the roadmap tiles (5e0) instead of satellite ones:
// the dark filter in Contact.scss only reads well on a flat map.
const MAP_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1385.2098649850752!2d44.761724562659985!3d41.72878379686667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x404472e102e66e39%3A0x13bd5dcf8a3d39e6!2zMjIg4YOS4YOj4YOg4YOQ4YObIOGDpOGDkOGDnOGDr-GDmOGDmeGDmOGDq-GDmOGDoSDhg6Xhg6Phg6nhg5AsIOGDl-GDkeGDmOGDmuGDmOGDoeGDmA!5e0!3m2!1ska!2sge!4v1753131007820!5m2!1ska!2sge";

const Contact = () => {
    const lang = currentLanguage();
    const strings = language[lang].contact;
    const page = language[lang].contactPage;

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const update = (field) => (event) => {
        const {value} = event.target;
        setFormData((prev) => ({...prev, [field]: value}));
        setFieldErrors((prev) => (prev[field] ? {...prev, [field]: undefined} : prev));
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (loading) return; // no duplicate submits while the request is in flight
        setLoading(true);
        setFieldErrors({});
        try {
            await axios.post('/api/contact', formData);
            notify.success({
                en: "Your message has been sent. We will get back to you shortly.",
                geo: "თქვენი შეტყობინება გაიგზავნა. მალე დაგიკავშირდებით.",
                ru: "Ваше сообщение отправлено. Мы скоро свяжемся с вами.",
                am: "Ձեր հաղորդագրությունն ուղարկվեց։ Շուտով կկապվենք ձեզ հետ։"
            });
            // The form is only cleared once the server confirmed the write.
            setFormData(EMPTY_FORM);
        } catch (err) {
            setFieldErrors(apiFieldErrors(err));
            notify.apiError(err, {
                en: "The message could not be sent. Please try again.",
                geo: "შეტყობინების გაგზავნა ვერ მოხერხდა. სცადეთ თავიდან.",
                ru: "Не удалось отправить сообщение. Попробуйте ещё раз.",
                am: "Հաղորդագրությունը չուղարկվեց։ Կրկին փորձեք։"
            });
        } finally {
            setLoading(false);
        }
    };

    const infoCards = [
        {
            icon: <TelephoneFill />,
            label: strings.call_us,
            value: <a className="contact-info-value" href={PHONE_HREF}>{PHONE}</a>
        },
        {
            icon: <EnvelopeFill />,
            label: strings.email_us,
            value: <a className="contact-info-value" href={`mailto:${EMAIL}`}>{EMAIL}</a>
        },
        {
            icon: <GeoAltFill />,
            label: strings.our_address,
            value: (
                <a className="contact-info-value" href={MAP_URL} target="_blank" rel="noreferrer">
                    {strings.address}
                </a>
            )
        },
        {
            icon: <Clock />,
            label: strings.opening_hours,
            value: <span className="contact-info-value">{strings.hours}</span>
        }
    ];

    const field = (name, label, {type = "text", textarea = false, required = true, placeholder = ""} = {}) => (
        <div className={`cx-field ${textarea ? "contact-field-wide" : ""}`}>
            <label className="cx-label" htmlFor={`contact-${name}`}>
                {label}{required && <span className="cx-required" aria-hidden="true">*</span>}
            </label>
            {textarea ? (
                <textarea
                    id={`contact-${name}`}
                    className={`cx-textarea ${fieldErrors[name] ? "cx-textarea-invalid" : ""}`}
                    value={formData[name]}
                    required={required}
                    rows={5}
                    placeholder={placeholder}
                    aria-invalid={Boolean(fieldErrors[name])}
                    onChange={update(name)}
                />
            ) : (
                <input
                    id={`contact-${name}`}
                    className={`cx-input ${fieldErrors[name] ? "cx-input-invalid" : ""}`}
                    type={type}
                    value={formData[name]}
                    required={required}
                    placeholder={placeholder}
                    aria-invalid={Boolean(fieldErrors[name])}
                    onChange={update(name)}
                />
            )}
            {fieldErrors[name] && <p className="cx-error-text">{fieldErrors[name]}</p>}
        </div>
    );

    useEffect(() => {
        window.scrollTo({top: 0, behavior: "smooth"});
    }, [])

    return (
        <div className="cx-page contact-page">
            <PageHero
                eyebrow={page.eyebrow}
                title={page.title}
                subtitle={page.subtitle}
            />

            <section className="cx-section cx-section-tight">
                <div className="container">
                    <div className="contact-info-grid">
                        {infoCards.map((card) => (
                            <div className="contact-info-card" key={card.label}>
                                <span className="contact-info-icon">{card.icon}</span>
                                <span className="contact-info-label">{card.label}</span>
                                {card.value}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="cx-section">
                <div className="container">
                    <div className="contact-layout">
                        <form className="contact-form" onSubmit={sendMessage}>
                            <div className="cx-section-head">
                                <h2 className="cx-section-title">{strings.get_in_touch}</h2>
                                <p className="cx-section-text">{page.form_note}</p>
                            </div>

                            <div className="contact-fields">
                                {field('name', strings.name, {placeholder: strings.name_placeholder})}
                                {field('email', strings.email, {type: "email", placeholder: strings.email_placeholder})}
                                {field('phone', strings.number, {type: "tel", placeholder: strings.number_placeholder})}
                                {field('subject', strings.subject, {placeholder: strings.subject_placeholder})}
                                {field('message', strings.message, {textarea: true, placeholder: strings.message_placeholder})}
                            </div>

                            <button type="submit" className="cx-btn cx-btn-primary contact-submit" disabled={loading}>
                                {loading
                                    ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> {strings.loading}</>
                                    : <><SendFill /> {strings.send_message}</>}
                            </button>
                        </form>

                        <aside className="contact-aside">
                            <div className="contact-map-card">
                                <div className="cx-section-head contact-map-head">
                                    <h2 className="cx-section-title">{strings.location}</h2>
                                </div>
                                <iframe
                                    className="contact-map"
                                    title={strings.location}
                                    src={MAP_EMBED}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                                <a className="cx-btn cx-btn-subtle cx-btn-block contact-map-link" href={MAP_URL} target="_blank" rel="noreferrer">
                                    <GeoAltFill /> {page.open_map}
                                </a>
                            </div>

                            <div className="contact-channels">
                                <h3 className="contact-channels-title">{page.channels_title}</h3>
                                <a className="cx-btn cx-btn-primary cx-btn-block" href={PHONE_HREF}>
                                    <TelephoneFill /> {page.call_button}
                                </a>
                                <a className="cx-btn cx-btn-ghost cx-btn-block" href={WHATSAPP_URL} target="_blank" rel="noreferrer">
                                    <Whatsapp /> {page.whatsapp_button}
                                </a>
                                <a className="cx-btn cx-btn-ghost cx-btn-block" href={`mailto:${EMAIL}`}>
                                    <EnvelopeFill /> {page.email_button}
                                </a>
                                <p className="contact-channels-hours">
                                    <Clock /> {strings.hours}
                                </p>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Contact
